import React, {useEffect, useMemo, useState} from "react";
import { Grid, Typography, Button } from "@mui/material";
import { Grid as DataGrid, GridColumn } from "@progress/kendo-react-grid";
import { orderBy } from "@progress/kendo-data-query";
import {RothIRACurrencyCellMaxAmountFutureAdjustments} from "../currency-cell";
import { PercentageCellOneHundredStocksBondsCash } from "../percentage-cell";
import {PercentageCell, AdvancedPercentageCell} from "../percentage-cell";

import { DropDownList } from "@progress/kendo-react-dropdowns";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import {PRETAX_ACCOUNTS, ROTH_CONVERSTION, ROTH_DESTINATION} from "../../shared/constants";
import {collectErrorMessages, currencyFormatNoDecimals, getCurrentYear} from "../../shared/shared";
import IconButton from "@mui/material/IconButton";
import ErrorIcon from "@mui/icons-material/Error";
import {StyledTooltip} from "../../pages/plan-inputs";
import ValidationSignCell from "../validation-sign-cell";
import OwnerJointTrustDropDown from "../owner-joint-trust-cell";
import YearCell from "../year-cell";
import {RILAUpsideDropDown, RILADownsideDropDown} from "../fia";
import {ROTH_DESTINATION_ROTH_IRA, ROTH_DESTINATION_VOLATILITY_BUFFER_FIA, ROTH_DESTINATION_INCREASING_PAYOUT_FIA, ROTH_DESTINATION_LEVEL_INCOME_FIA, ROTH_DESTINATION_RILA} from "../../shared/constants";

const CommandCell = (deleteFn) => {
  return (props) => {
    const dataItem = props.dataItem;

    return (
      <td>
        <DeleteIcon
          onClick={() => {
            deleteFn(dataItem);
          }}
        />
      </td>
    );
  };
};

const YearInputCell = (props) => {

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  return <td>
    <NumericTextBox
      value={dataItem[field]}
      onChange={(e) => {
        RothIraDetails.update(field)(dataItem, e.target.value);
      }}
      valid={dataValue > 0}
    />
  </td>
}

const RothIraDetails = (props) => {
  const dataItem = props.dataItem;

  return (
    <DataGrid
      data={[dataItem]}
      scrollable={"none"}
      editField={"inEdit"}
      onItemChange={(event) => {
        const dataItem = event.dataItem;
        const field = event.field;
        const value = event.value;
        RothIraDetails.update(field)(dataItem, value);
      }}
    >
      <GridColumn
        title={"Stocks"}
        field={"stocks"}
        cell={PercentageCellOneHundredStocksBondsCash}
      />
      <GridColumn
        title={"Bonds"}
        field={"bonds"}
        cell={PercentageCellOneHundredStocksBondsCash}
      />
      <GridColumn
        title={"Cash"}
        field={"cash"}
        cell={PercentageCellOneHundredStocksBondsCash}
      />
    </DataGrid>
  );
};

const RothIraVolatilityBufferDetails = (props) => {
  const dataItem = props.dataItem;

  const upsideRateInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      if (item.upsideType === "PAR") {
        return item.upsideRate >= 0 && item.upsideRate <= 200;
      }

      return item.upsideRate >=0 && item.upsideRate <= 20;
    });
  }, []);

  return <DataGrid
      data={[dataItem]}
      scrollable={"none"}
      editField={"inEdit"}
      onItemChange={(event) => {
        const dataItem = event.dataItem;
        const field = event.field;
        const value = event.value;
        RothIraDetails.update(field)(dataItem, value);
      }}
  >
    <GridColumn
        title={"Premium Bonus"}
        field={"premiumBonus"}
        cell={PercentageCell}
    />
    <GridColumn
      title={"Upside"}
      field={"upsideType"}
      cell={RILAUpsideDropDown}
    />
    <GridColumn
        title={" "}
        field={"upsideRate"}
        cell={upsideRateInput}
    />
    <GridColumn
        title={"Downside"}
        field={"downsideType"}
        editable={false}
    />
    <GridColumn
        title={" "}
        field={"downsideRate"}
        format={"{0:p}"}
        editable={false}
    />
    <GridColumn
      title={"Avg Return"}
      field={"averageReturn"}
      editable={false}
      format={"{0:p}"}
    />
    <GridColumn
      title={"Std. Deviation"}
      field={"standardDeviation"}
      editable={false}
      format={"{0:p}"}
    />
  </DataGrid>
};

const RothIraLevelIncomeDetails = (props) => {
  const dataItem = props.dataItem;

  const payoutFactorsInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
         return item.payoutFactors >=0 && item.payoutFactors <= 15;
    });
  }, []);

  return <DataGrid
      data={[dataItem]}
      scrollable={"none"}
      editField={"inEdit"}
      onItemChange={(event) => {
        const dataItem = event.dataItem;
        const field = event.field;
        const value = event.value;
        RothIraDetails.update(field)(dataItem, value);
      }}
  >
    <GridColumn
      field={"premiumBonus"}
      title={"Premium Bonus"}
      cell={PercentageCell}
    />
    <GridColumn
      field={"hypotheticalIndexReturn"}
      title={"Hypothetical Index Return"}
      cell={PercentageCell}
    />
    <GridColumn
      field={"yearsOfDeferral"}
      title={"Years of Deferral"}
      cell={YearInputCell}
    />
    <GridColumn
      field={"baseRollUpRate"}
      title={"Base Roll Up Rate"}
      cell={PercentageCell}
    />
    <GridColumn
      field={"benefitBaseBonus"}
      title={"Benefit Base Bonus"}
      cell={PercentageCell}
    />
    <GridColumn
      field={"payoutFactors"}
      title={"Payout Factors"}
      cell={payoutFactorsInput}
    />
  </DataGrid>
};

const RothIraIncreasingPayoutDetails = (props) => {
  const dataItem = props.dataItem;

  const payoutFactorsInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      return item.payoutFactors >=0 && item.payoutFactors <= 20;
    });
  }, []);

  const baseRollUpRateInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      return item.baseRollUpRate >=0 && item.baseRollUpRate <= 20;
    });
  }, []);

  const benefitBaseInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      return item.benefitBaseBonus >=0 && item.benefitBaseBonus <= 20;
    });
  }, []);

  const growthMultiplierInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      return item.growthMultiplier >=0 && item.growthMultiplier <= 15;
    });
  }, []);

  const stackingMultiplierInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      return item.stackingMultiplier >=0 && item.stackingMultiplier <= 500;
    });
  }, []);

  return <DataGrid
      data={[dataItem]}
      scrollable={"none"}
      editField={"inEdit"}
      onItemChange={(event) => {
        const dataItem = event.dataItem;
        const field = event.field;
        const value = event.value;
        RothIraDetails.update(field)(dataItem, value);
      }}
  >
    <GridColumn
        field={"premiumBonus"}
        title={"Premium Bonus"}
        cell={PercentageCell}
    />
    <GridColumn
        field={"hypotheticalIndexReturn"}
        title={"Hypothetical Index Return"}
        cell={PercentageCell}
    />
    <GridColumn
        field={"yearsOfDeferral"}
        title={"Years of Deferral"}
        cell={YearInputCell}
    />
    <GridColumn
        field={"baseRollUpRate"}
        title={"Base Roll Up Rate"}
        cell={baseRollUpRateInput}
    />
    <GridColumn
        field={"benefitBaseBonus"}
        title={"Benefit Base Bonus"}
        cell={benefitBaseInput}
    />
    <GridColumn
        field={"payoutFactors"}
        title={"Payout Factors"}
        cell={payoutFactorsInput}
    />
    <GridColumn
      field={"growthMultiplier"}
      title={"Growth Multiplier"}
      cell={growthMultiplierInput}
    />
    <GridColumn
      field={"stackingMultiplier"}
      title={"Stacking Multiplier"}
      cell={stackingMultiplierInput}
    />
  </DataGrid>
};

const RothIraRILADetails = (props) => {
  const dataItem = props.dataItem;

  const upsideRateInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      if (item.upsideType === "PAR") {
        return item.upsideRate >= 0 && item.upsideRate <= 200;
      }

      return item.upsideRate >=0 && item.upsideRate <= 20;
    });
  }, []);

  const downsideRateInput = useMemo(() => {
    return AdvancedPercentageCell((item) => {
      if (item.downsideType === "PAR") {
        return item.downsideRate >= 0 && item.downsideRate <= 100;
      }

      return item.downsideRate >= -30 && item.downsideRate <= 0;
    });
  }, []);


  return <DataGrid
      data={[dataItem]}
      scrollable={"none"}
      editField={"inEdit"}
      onItemChange={(event) => {
        const dataItem = event.dataItem;
        const field = event.field;
        const value = event.value;
        RothIraDetails.update(field)(dataItem, value);
      }}
  >
    <GridColumn
        title={"Premium Bonus"}
        field={"premiumBonus"}
        cell={PercentageCell}
    />
    <GridColumn
        title={"Upside"}
        field={"upsideType"}
        cell={RILAUpsideDropDown}
    />
    <GridColumn
        title={" "}
        field={"upsideRate"}
        cell={upsideRateInput}
    />
    <GridColumn
        title={"Downside"}
        field={"downsideType"}
        cell={RILADownsideDropDown}
    />
    <GridColumn
        title={" "}
        field={"downsideRate"}
        cell={downsideRateInput}
    />
    <GridColumn
        title={"Avg Return"}
        field={"averageReturn"}
        editable={false}
        format={"{0:p}"}
    />
    <GridColumn
        title={"Std. Deviation"}
        field={"standardDeviation"}
        editable={false}
        format={"{0:p}"}
    />
  </DataGrid>
};

const RothConversionDetails = (props) => {
  const dataItem = props.dataItem;

  switch(dataItem.destination) {
    case ROTH_DESTINATION_ROTH_IRA: return <RothIraDetails {...props} />;
    case ROTH_DESTINATION_VOLATILITY_BUFFER_FIA: return <RothIraVolatilityBufferDetails {...props} />;
    case ROTH_DESTINATION_LEVEL_INCOME_FIA: return <RothIraLevelIncomeDetails {...props} />;
    case ROTH_DESTINATION_INCREASING_PAYOUT_FIA: return <RothIraIncreasingPayoutDetails {...props} />;
    case ROTH_DESTINATION_RILA: return <RothIraRILADetails {...props} />;
    default:
      return <RothIraDetails {...props} />
  }
};


const RothConversionTypeDropDown = (props) => {
  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.value,
      });
    }
  };

  const options = ROTH_CONVERSTION;

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];
  return (
    <td>
      <DropDownList
        style={{
          width: "100px",
        }}
        onChange={handleChange}
        value={options.find((c) => c.value === dataValue)}
        data={options}
        textField="text"
      />
    </td>
  );
};

const RothConversionDestinationDropDown = (props) => {
  const handleChange = (e) => {
    if (props.onChange) {
      RothConversionDestinationDropDown.updateDefaults({
         ...props.dataItem,
         [props.field]: e.target.value.value
       });
    }
  };

  const options = ROTH_DESTINATION;

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];
  return (
      <td>
        <DropDownList
            style={{
              width: "100px",
            }}
            onChange={handleChange}
            value={options.find((c) => c.value === dataValue)}
            data={options}
            textField="text"
        />
      </td>
  );
};

export const AccountTransferDropDown = (accounts) => (props) => {
  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.value,
      });
    }
  };

  const preTaxAccounts = accounts.filter((item) => item.taxType === "PRE-TAX").filter(item => {
    if (item.owner === "TRUST") {
      return props.dataItem.owner.toUpperCase() === item.trustName.toUpperCase() && item.id != null;
    }
    return item.owner === props.dataItem.owner && item.id != null;
  });

  let typeMap = {};
  const options = preTaxAccounts
      .map((item) => {
        if (typeMap[props.dataItem.owner + item.taxType + item.value]) {
          typeMap[props.dataItem.owner + item.taxType + item.value].push(typeMap[props.dataItem.owner + item.taxType + item.value].length + 1);
        } else {
          typeMap[props.dataItem.owner + item.taxType + item.value] = [1];
        }
        return item;
      })
      .map((item) => {
        const option = PRETAX_ACCOUNTS.find((v) => v.value === item.accountType);
        if (option.value === "TE_OTHER" && item.accountTypeOtherName) {
          option.text = item.accountTypeOtherName;
        }

        let text = "";
        if(option != undefined) {
          text = option.text + " - " + currencyFormatNoDecimals(item.value)
        }
        if (typeMap[props.dataItem.owner + item.taxType + item.value].length == 1 && typeMap[props.dataItem.owner + item.taxType + item.value][0] == 1) {
          typeMap[props.dataItem.owner + item.taxType + item.value].shift();
        } else if (typeMap[props.dataItem.owner + item.taxType + item.value].length > 0) {
          text = text + " - " + typeMap[props.dataItem.owner + item.taxType + item.value][0];
          typeMap[props.dataItem.owner + item.taxType + item.value].shift();
        }
        return {
          value: item.id,
          text: text,
        };
  });

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];
  return (
    <td>
      <DropDownList
        onChange={handleChange}
        value={options.find((c) => c.value === dataValue)}
        data={options}
        textField="text"
        valid={dataValue !== null && dataValue !== undefined && dataValue !== ""}
      />
    </td>
  );
};

const RothIra = (props) => {
  const [state, setState] = useState({
    sort: [
      {
        field: "firstName",
        dir: "asc",
      },
    ],
    page: { skip: 0, take: 10 },
    errorMessages: [],
  });

  useEffect(() => {
    setState({
      ...state,
      errorMessages: collectErrorMessages(props.validations.rothTransfers, "roth transfer"),
    })
  }, [props.validations.rothTransfers]);

  const onExpandChange = (event) => {
    props.update("isExpanded")(event.dataItem, !event.dataItem.isExpanded);
  };

  const addRothIra = () => {

    const currentYear = parseInt((new Date()).getFullYear());

    const prototype = {
      accountId: null,
      bonds: 50,
      cash: 0,
      conversionType: "ALL",
      transferStartYear: currentYear,
      noOfYears: 1,
      stocks: 50,
      transferAmount: 0,
      transferId: null,
      owner: "CLIENT",
      editIndex: 0,
      inEdit: true,
      isExpanded: true,
      destination: "ROTH_IRA",
      premiumBonus: -10,
      upsideType: "PAR",
      upsideRate: 50,
      downsideType: "FLOOR",
      downsideRate: 0,
      averageReturn: props.sandpReturns.averageReturn / 100,
      standardDeviation: props.sandpReturns.standardDeviation / 100,
      yearsOfDeferral: 0,
      baseRollUpRate: 0,
      benefitBaseBonus: 0,
      hypotheticalIndexReturn: 0,
      payoutFactors: 0,
      growthMultiplier: 0,
      stackingMultiplier: 0,
      transferEndYear: currentYear,
    };

    props.addTransfer(prototype);
  };

  const AccountTransferDDL = AccountTransferDropDown(props.accounts);
  OwnerJointTrustDropDown.hasSpouse = props.client.hasSpouse;
  RothIraDetails.update = props.update;

  RothConversionDestinationDropDown.updateDefaults = (dataItem) => {

    const currentYear = (new Date()).getFullYear();

    const setRothIRADefaults = () => {
      props.updateItemInCollection({
        ...dataItem,
        transferAmount: 0,
        stocks: 50,
        bonds: 50,
        cash: 0
      });
    };

    const setVolatilityBufferFIADefaults = () => {
      props.updateItemInCollection({
        ...dataItem,
        premiumBonus: -10,
        upsideType: "PAR",
        upsideRate: 50,
        downsideType: "FLOOR",
        downsideRate: 0,
        averageReturn: props.sandpReturns.averageReturn / 100,
        standardDeviation: props.sandpReturns.standardDeviation / 100,
      })
    };

    const setLevelIncomeFIADefaults = () => {
      props.updateItemInCollection({
        ...dataItem,
        transferAmount: 0,
        premiumBonus: 0,
        yearsOfDeferral: 0,
        baseRollUpRate: 0,
        benefitBaseBonus: 0,
        hypotheticalIndexReturn: 0,
        payoutFactors: 4,
        transferEndYear: currentYear
      });
    };

    const setIncreasingPayoutFIADefaults = () => {
      props.updateItemInCollection({
        ...dataItem,
        transferAmount: 0,
        premiumBonus: 0,
        yearsOfDeferral: 0,
        baseRollUpRate: 6.5,
        benefitBaseBonus: 8,
        hypotheticalIndexReturn: 0,
        payoutFactors: 4,
        growthMultiplier: 10,
        stackingMultiplier: 150,
        transferEndYear: currentYear
      });
    };

    const setRILADefaults = () => {
      props.updateItemInCollection({
        ...dataItem,
        transferAmount: 0,
        transferEndYear: currentYear,
        premiumBonus: 0,
        upsideType: "PAR",
        upsideRate: 50,
        downsideType: "PAR",
        downsideRate: 50,
        averageReturn: props.sandpReturns.averageReturn / 100,
        standardDeviation: props.sandpReturns.standardDeviation / 100,
      });
    };

    switch(dataItem.destination) {
      case ROTH_DESTINATION_ROTH_IRA: setRothIRADefaults(); break;
      case ROTH_DESTINATION_VOLATILITY_BUFFER_FIA: setVolatilityBufferFIADefaults(); break;
      case ROTH_DESTINATION_LEVEL_INCOME_FIA: setLevelIncomeFIADefaults(); break;
      case ROTH_DESTINATION_INCREASING_PAYOUT_FIA: setIncreasingPayoutFIADefaults(); break;
      case ROTH_DESTINATION_RILA: setRILADefaults(); break;
    }
  }

  const rothTransfers = props.rothTransfers.map((item, index) => {
    const validation = props.validations.rothTransfers[index];
    let isValid = true;
    if (validation) {
      Object.keys(validation).forEach((key) => {
        if (!validation[key].valid) isValid = false;
      });
    }
    return {
      ...item,
      inEdit: true,
      isValid: isValid,
      showJointTrustOptions: false,
      listTrusts: false
    }
  });

  OwnerJointTrustDropDown.trustList = props.trustListing;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography>Roth Conversion</Typography>
      </Grid>
      <Grid item xs={12}>
        {(!props.readOnly && state.errorMessages.length > 0) && (
            <div className="error-tooltip-wrapper">
              <StyledTooltip
                  arrow
                  className="error-tooltip"
                  placement="right"
                  title={
                    <ul className="error-tooltip-ul">{
                      state.errorMessages.map((e, idx) => {
                        return (
                            <li key={idx}>
                              <Typography
                                  className={'default-font'}
                                  textAlign={'left'}
                                  variant={'span'}> {e}
                              </Typography>
                            </li>
                        );
                      })}
                    </ul>
                  }
              >
                <IconButton>
                  <ErrorIcon />
                </IconButton>
              </StyledTooltip>
            </div>
        )}
        <Button onClick={addRothIra}>
          <AddIcon /> Add Roth Conversion
        </Button>
      </Grid>
      <Grid item xs={12}>
        <DataGrid
          data={orderBy(rothTransfers, state.sort).slice(
            state.page.skip,
            state.page.take + state.page.skip
          )}
          skip={state.page.skip}
          take={state.page.take}
          total={props.rothTransfers.length}
          pageable={true}
          onPageChange={(event) => {
            setState({
              ...state,
              page: event.page,
            });
          }}
          editField={"inEdit"}
          scrollable={"none"}
          expandField={"isExpanded"}
          onExpandChange={onExpandChange}
          detail={RothConversionDetails}
          onItemChange={(event) => {
            const dataItem = event.dataItem;
            const field = event.field;
            const value = event.value;

            props.update(field)(dataItem, value);
          }}
        >
          <GridColumn title={" "} field={"isValid"} cell={ValidationSignCell} />
          <GridColumn title={"Owner"} field={"owner"} cell={OwnerJointTrustDropDown} />
          <GridColumn
            field={"accountId"}
            title={"Transfer Account"}
            cell={AccountTransferDDL}
          />
          <GridColumn
            field={"conversionType"}
            title={"Conversion Type"}
            cell={RothConversionTypeDropDown}
          />
          <GridColumn
            field={"noOfYears"}
            title={"Number of Years"}
            cell={YearInputCell}
          />
          <GridColumn
              field={"transferStartYear"}
              title={"Start Year"}
              cell={YearCell}
          />
          <GridColumn
            field={"transferAmount"}
            title={"Transfer Amount"}
            editable={true}
            cell={RothIRACurrencyCellMaxAmountFutureAdjustments}
          />
          <GridColumn title={" "} cell={CommandCell(props.delete)} />
        </DataGrid>
      </Grid>
    </Grid>
  );
};

export default RothIra;
